import React, { useCallback, useEffect, useMemo, useState, forwardRef } from 'react';
import styled from 'styled-components';
import { IconButton, MdiIcon, Tooltip } from '@mic3/platform-ui';
import { Feature } from 'ol';
import { fromLonLat, toLonLat } from 'ol/proj';
import { LineString } from 'ol/geom';
import { Stroke, Style } from 'ol/style';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import genUuidv4 from 'uuid/v4';
import { useSelector } from 'react-redux';
import Modify from 'ol/interaction/Modify';
import Collection from 'ol/Collection';

import olMap from 'app/containers/Maps/SituationalAwareness/olMapController';
import BaseLayerDropdown from 'app/components/molecules/Map/BaseLayerDropdown';
import { MAP_MARKER_LAYER, DEFAULT_ZOOM_LEVEL } from 'app/utils/maps/layer/layerUtils';

const MapToolbar = styled.div`
    position: absolute;
    top: 10px;
    z-index: 20;
    right: 10px;
    border-radius: 40px;
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.material.colors.background.default};
`;

const MapWrapper = styled.div`
    width: 100%;
    ${({ isFullScreen }) =>
    isFullScreen
        ? `
        position: fixed;
        z-index: 300;
        top: ${isFullScreen ? 49 : 58}px;
        right: 0;
        width: 100%;
        height: calc(100vh - ${isFullScreen ? 114 : 58}px);
        background-color: white;
    `
        : `
            height: 300px;
            position: relative;

        `};
    .ol-zoom {
        right: 2.5rem !important;
    }
    .scaleUnitClass {
        display: none;
    }
`;

const LocationMap = forwardRef(({ markers, mapId, onChange, allowPinDrag }, ref) => {
    const [baseLayer, setBaseLayer] = useState();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const mapSettings = useSelector((state) => state.app.configurations.affectli.map || {});
    const { defaultBaseLayerData } = mapSettings || {};
    const map = useMemo(() => new olMap(), []);
    const _mapId = useMemo(() => mapId + '_' + genUuidv4(), [mapId]);

    const renderPolyline = useCallback(
        (markers) => {
            if (!map || markers.length < 2) return;
            const coordinates = markers.map((marker) => fromLonLat([marker.lng, marker.lat]));
            const geodesicFeature = new Feature({ geometry: new LineString(coordinates) });
            const nonGeodesicFeature = new Feature({ geometry: new LineString(coordinates) });
            const geodesicStyle = new Style({
                stroke: new Stroke({
                    color: '#00a1e1',
                    width: 4,
                }),
            });
            const nonGeodesicStyle = new Style({
                stroke: new Stroke({
                    color: '#e4e4e4',
                    width: 3,
                    opacity: 0.7,
                }),
            });
            const geodesicSource = new VectorSource({ features: [geodesicFeature] });
            const nonGeodesicSource = new VectorSource({ features: [nonGeodesicFeature] });
            const geodesicLayer = new VectorLayer({ source: geodesicSource, style: geodesicStyle });
            const nonGeodesicLayer = new VectorLayer({ source: nonGeodesicSource, style: nonGeodesicStyle });
            map.addLayer(geodesicLayer);
            map.addLayer(nonGeodesicLayer);
            const extent = geodesicFeature.getGeometry().getExtent();
            map.getView().fit(extent, { padding: [50, 50, 50, 50] });
        },
        [map]
    );

    const setupDraggableMarker = useCallback(() => {
        if (!allowPinDrag || !map || !markers?.[0]) return;

        const olMap = map.getMap();
        const markerLayer = olMap
            .getLayers()
            .getArray()
            .find((layer) => layer.get('title') === MAP_MARKER_LAYER);

        if (!markerLayer) return;

        const markerFeatures = markerLayer.getSource().getFeatures();
        if (markerFeatures.length === 0) return;
        const modify = new Modify({
            features: new Collection(markerFeatures),
            pixelTolerance: 15,
        });
        modify.on('modifyend', (event) => {
            const feature = event.features.getArray()[0];
            const coordinates = toLonLat(feature.getGeometry().getCoordinates());
            onChange?.({
                lng: coordinates[0],
                lat: coordinates[1],
            });
        });

        olMap.addInteraction(modify);
        return () => {
            olMap.removeInteraction(modify);
        };
    }, [map, onChange, markers, allowPinDrag]);

    const centerMap = useCallback(() => {
        if (!map || !markers?.[0]) return;

        const olMap = map.getMap();
        const view = olMap.getView();
        
        if (markers.length === 1) {
            const coordinates = fromLonLat([markers[0].lng, markers[0].lat]);
            view.animate({
                center: coordinates,
                duration: 500
            });
        } else if (markers.length > 1) {
            const coordinates = markers.map(marker => fromLonLat([marker.lng, marker.lat]));
            const extent = new LineString(coordinates).getExtent();
            view.fit(extent, { 
                padding: [50, 50, 50, 50],
                duration: 500
            });
        }
    }, [map, markers]);

    useEffect(() => {
        if (ref) {
            ref.current = {
                centerMap
            };
        }
    }, [ref, centerMap]);

    useEffect(() => {
        map.initEntityAboutMap(_mapId, `locationMap_${_mapId}`);
        setBaseLayer(defaultBaseLayerData);
    }, [map, defaultBaseLayerData, _mapId]);

    useEffect(() => {
        if (markers?.length && mapSettings?.defaultBaseLayer) {
            const olMapInstance = map.getMap();
            const existingMarkerLayer = olMapInstance
                .getLayers()
                .getArray()
                .find((layer) => layer.get('title') === MAP_MARKER_LAYER);
            
            const currentZoom = olMapInstance.getView().getZoom();
            const hasUserZoomedIn = currentZoom && currentZoom > DEFAULT_ZOOM_LEVEL;
            
            if (existingMarkerLayer) {
                olMapInstance.removeLayer(existingMarkerLayer);
            }
            markers.forEach((m) => {
                map.addPinsToAboutMap({ 
                    latitude: m?.lat, 
                    longitude: m?.lng,
                    preserveZoom: hasUserZoomedIn 
                });
            });
            if (markers.length > 1) {
                renderPolyline(markers);
            }
            const setDraggableMarker = setupDraggableMarker();
            return () => {
                setDraggableMarker?.();
            };
        }
    }, [markers, map, renderPolyline, mapSettings?.defaultBaseLayer, setupDraggableMarker, allowPinDrag]);

    const handleBaseLayerChange = (layer) => {
        setBaseLayer(layer);
    };

    const handleFullScreenClick = () => {
        setIsFullScreen(!isFullScreen);
    };

    return (
        <MapWrapper id={_mapId} isFullScreen={isFullScreen}>
            <MapToolbar>
                <Tooltip title='Full Screen Map' placement='bottom' arrow>
                    <IconButton onClick={handleFullScreenClick}>
                        <MdiIcon name='fullscreen' />
                    </IconButton>
                </Tooltip>
                <BaseLayerDropdown activeLayer={baseLayer} onLayerChange={handleBaseLayerChange} mapInstance={map} />
            </MapToolbar>
        </MapWrapper>
    );
});

export default LocationMap;
